import logo from 'assets/Talo-Logo.webp';
import logoWhite from 'assets/Talo-Logo-White.webp';
import Button from 'components/buttons/Button';
import { ChevronRight, Menu, UserRound, X } from 'lucide-react';
import { ROUTES } from 'config/route-configuration';
import { useAuth } from 'contexts/AuthContext';
import { useEffect, useState } from 'react';
import { fetchUserAttributes, signOut } from 'aws-amplify/auth';

const navItems = [{ label: 'Explore treatments', href: ROUTES.EDTREATMENT }];

const NavBar = () => {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let isActive = true;
    const loadName = async () => {
      if (!user) {
        setFirstName(null);
        return;
      }
      try {
        const attrs = await fetchUserAttributes();
        if (!isActive) return;
        setFirstName(attrs.given_name ?? null);
      } catch {
        if (!isActive) return;
        setFirstName(null);
      }
    };

    loadName();
    return () => {
      isActive = false;
    };
  }, [user]);

  return (
    <div className="w-full bg-talo-blue-700 sm:bg-white relative mb-4 sm:mb-0">
      <div className="mx-auto flex h-[72px] items-center justify-between px-6 text-white sm:text-gray-900">
        <div className="flex items-center gap-3">
          <a href={ROUTES.HOME} aria-label="Talo home">
            <img src={logo} alt="Talo logo" className="hidden h-10 w-auto sm:block" />
            <img src={logoWhite} alt="Talo logo" className="h-10 w-auto sm:hidden" />
          </a>
        </div>

        <nav className="hidden items-center gap-8 text-sm font-medium text-gray-800 sm:flex">
          {navItems.map(({ label, href }) => (
            <a key={label} href={href} className="transition-colors duration-150 hover:text-gray-600">
              {label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-4 sm:flex">
          <Button text="Get started" link={ROUTES.EDQUESTIONNAIRE} />
          {firstName && <span className="text-sm font-semibold text-brand-black">Hi, {firstName}</span>}
          <a
            aria-label="Account"
            href={user ? ROUTES.ACCOUNT : ROUTES.SIGNUP}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-talo-blue-700 text-white transition hover:border-gray-300 hover:bg-[#152c2b]"
          >
            <UserRound size={20} />
          </a>
        </div>

        <button
          type="button"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          className="flex h-10 w-10 items-center justify-center rounded-full text-white transition sm:hidden"
          onClick={() => setMobileOpen((prev) => !prev)}
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="mb-4 rounded-b-3xl bg-talo-blue-700 px-6 py-6 text-white transition-all duration-200 ease-out sm:hidden">
          <div className="space-y-6">
            <div>
              <span className="inline-flex rounded-md bg-white px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-talo-blue-700">
                Treatments
              </span>
              <div className="mt-3 space-y-3">
                <a
                  href={ROUTES.EDTREATMENT}
                  className="flex items-center justify-between text-sm font-semibold text-white"
                  onClick={() => setMobileOpen(false)}
                >
                  Erectile dysfunction
                  <ChevronRight size={18} className="text-white/70" />
                </a>
                <a
                  href={ROUTES.WEIGHTLOSSTREATMENT}
                  className="flex items-center justify-between text-sm font-semibold text-white"
                  onClick={() => setMobileOpen(false)}
                >
                  Weight loss
                  <ChevronRight size={18} className="text-white/70" />
                </a>
              </div>
            </div>

            <div>
              <span className="inline-flex rounded-md bg-white px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-talo-blue-700">
                Account
              </span>
              <div className="mt-3 space-y-3">
                {firstName && <p className="text-sm text-white/80">Hi, {firstName}</p>}
                {user ? (
                  <>
                    <a
                      href={ROUTES.ACCOUNTDELIVERIES}
                      className="flex items-center justify-between text-sm font-semibold text-white"
                      onClick={() => setMobileOpen(false)}
                    >
                      Orders
                      <ChevronRight size={18} className="text-white/70" />
                    </a>
                    <a
                      href={ROUTES.ACCOUNTSUBSCRIPTIONS}
                      className="flex items-center justify-between text-sm font-semibold text-white"
                      onClick={() => setMobileOpen(false)}
                    >
                      Subscriptions
                      <ChevronRight size={18} className="text-white/70" />
                    </a>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between text-sm font-semibold text-white"
                      onClick={async () => {
                        await signOut();
                        setMobileOpen(false);
                        window.location.href = ROUTES.HOME;
                      }}
                    >
                      Log out
                      <ChevronRight size={18} className="text-white/70" />
                    </button>
                  </>
                ) : (
                  <a
                    href={ROUTES.SIGNUP}
                    className="flex items-center justify-between text-sm font-semibold text-white"
                    onClick={() => setMobileOpen(false)}
                  >
                    Log in
                    <ChevronRight size={18} className="text-white/70" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavBar;
